import type { ChaseTenant, PortfolioTenant, Tenant } from './api.ts';
import { DAY_MS } from './clientRows.ts';

/**
 * The three views the Clients screen offers, decided here rather than in the
 * component that draws them.
 *
 * The screen has to assemble one list out of two endpoints that disagree about
 * what a client list is. `GET /portfolio` carries the assessment figures and
 * hard-filters archived clients away in SQL; `GET /tenants?includeArchived=true`
 * carries the archived ones and the contact details but knows nothing about any
 * assessment. Neither is wrong, and neither can be made to answer for the other
 * without a server change, so the console asks both and joins them — and the
 * join is the part worth testing, which is why it is a plain module and not a
 * hook. The suite is `.ts`-only (see the note in vitest.config.ts), so logic
 * that lives in a `.tsx` component is logic nothing can assert about.
 */

/** Which view the tab strip is showing. */
export type ClientViewId = 'live' | 'awaiting' | 'archived';

export interface ClientViews {
  /** Every client that is not archived, assessment figures included. */
  readonly live: readonly PortfolioTenant[];
  /**
   * The subset of `live` that needs a consent link. A subset, not a fourth
   * state: a client awaiting consent is a live client, it is simply one nobody
   * can assess yet, and moving it out of the main list would hide a rollout
   * from the screen that is supposed to show the estate.
   *
   * Two connection states, not one. A client who has never consented and a
   * client who has withdrawn consent are the same job — copy a link, ask their
   * Global Administrator again — and a tab that held only the first would leave
   * the second with nowhere to be worked from.
   */
  readonly awaiting: readonly PortfolioTenant[];
  /** Archived clients, which carry no assessment figures at all. */
  readonly archived: readonly Tenant[];
  /**
   * The bucket sizes, named once. Every tab label carries its count, and a
   * label computed from a different filter than the table underneath it is a
   * lie the screen cannot detect — so the label and the table read the same
   * arrays.
   */
  readonly counts: Readonly<Record<ClientViewId, number>>;
}

/**
 * Join the two responses into the three buckets.
 *
 * Archived rows are taken from the `listTenants` response and identified by
 * `archivedAt`, never by which endpoint they arrived from: the portfolio query
 * excludes them today, and this function stays right if that ever stops being
 * true. Anything archived is removed from the live bucket by id for the same
 * reason — a client appearing in both would be counted twice and offered an
 * Archive button it has already had.
 */
export const clientViews = (
  portfolio: readonly PortfolioTenant[],
  listed: readonly Tenant[],
): ClientViews => {
  const archived = listed.filter((tenant) => tenant.archivedAt !== null);
  const archivedIds = new Set(archived.map((tenant) => tenant.id));

  const live = portfolio.filter(
    (tenant) => tenant.archivedAt === null && !archivedIds.has(tenant.id),
  );
  const awaiting = live.filter(
    (tenant) =>
      tenant.connectionState === 'pending_consent' ||
      tenant.connectionState === 'consent_revoked',
  );

  return {
    live,
    awaiting,
    archived,
    counts: { live: live.length, awaiting: awaiting.length, archived: archived.length },
  };
};

/**
 * One row of the Awaiting consent tab: the client, and whatever the chase
 * payload could add to it.
 */
export interface AwaitingRow {
  /** The client itself. Everything the row renders that is not below comes from here. */
  readonly tenant: PortfolioTenant;
  /**
   * The `GET /tenants/onboarding` entry for this client, or null when the
   * server left it out. Enrichment only — it carries the consent link and the
   * server's own `daysWaiting`, and it decides nothing.
   */
  readonly chase: ChaseTenant | null;
  /**
   * Whole days since the link went out, or null when nothing has been sent and
   * the chase payload did not say. `Tenant` carries no creation date, so an
   * un-invited client's wait is only knowable from the server.
   */
  readonly daysWaiting: number | null;
}

/**
 * How long this client has been waiting, from whichever source can answer.
 *
 * The server measures from `invitedAt` or, failing that, from the row's
 * creation date — which the console is never told. So a client the chase
 * payload left out is measured from `invitedAt` here, and a client with no
 * `invitedAt` and no chase entry has a wait nothing on this side can compute.
 * That combination should not arise (a client nobody has invited has no
 * reminders either, so the server's filters cannot exclude it), and it is
 * answered with null rather than with a zero that would read as "added today".
 */
const waitingDays = (
  tenant: PortfolioTenant,
  chase: ChaseTenant | null,
  now: number,
): number | null => {
  if (chase !== null) return chase.daysWaiting;
  if (tenant.invitedAt === null) return null;
  const sent = Date.parse(tenant.invitedAt);
  return Number.isNaN(sent) ? null : Math.max(0, Math.floor((now - sent) / DAY_MS));
};

/**
 * Join the chase payload onto the clients that need a consent link.
 *
 * **The chase list is not the membership.** `GET /tenants/onboarding` answers a
 * narrower question than this tab asks: it is who to *nudge next*, so the server
 * drops any client chased within the last seven days or already reminded four
 * times. Treating it as the set of clients needing a link would quietly hide the
 * client chased yesterday — the one an operator is most likely to be looking
 * for — and the screen would look complete while doing it. So membership comes
 * from the portfolio, by connection state, and this payload only adds what it
 * knows: the consent link and the server's own count of days.
 *
 * A row whose `chase` is null is therefore an ordinary row, not a missing one.
 *
 * Longest waiting first, which is the order the work is done in. Sorted here
 * rather than left to the server, because half the rows may not have come from
 * it and a list ordered by two different rules is ordered by neither. A client
 * whose wait is unknown sorts last: it is not a claim that they arrived today.
 */
export const awaitingRows = (
  awaiting: readonly PortfolioTenant[],
  chaseList: readonly ChaseTenant[],
  now: number,
): readonly AwaitingRow[] => {
  const byId = new Map(chaseList.map((entry) => [entry.id, entry]));
  const rows = awaiting.map((tenant) => {
    const chase = byId.get(tenant.id) ?? null;
    return { tenant, chase, daysWaiting: waitingDays(tenant, chase, now) };
  });

  return [...rows].sort(
    (a, b) =>
      (b.daysWaiting ?? -1) - (a.daysWaiting ?? -1) ||
      a.tenant.displayName.localeCompare(b.tenant.displayName, 'en-GB'),
  );
};
